import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Microphone, ArrowsClockwise, Sparkle, ShieldCheck } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { apiClient } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";

const EMOTION_TAGS = [
  { tag: "[whispers]", key: "tagWhispers" },
  { tag: "[laughs]", key: "tagLaughs" },
  { tag: "[sighs]", key: "tagSighs" },
  { tag: "[excited]", key: "tagExcited" },
  { tag: "[sad]", key: "tagSad" },
  { tag: "[shouts]", key: "tagShouts" },
];

export default function VoiceGenerator({ onGenerated, credits }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [model, setModel] = useState("v3");
  const [generating, setGenerating] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    apiClient.get("/voices").then(({ data }) => {
      setVoices(data);
      if (data.length && !voiceId) setVoiceId(data[0].voice_id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const insertTag = (tag) => {
    const el = textRef.current;
    if (!el) {
      setText((v) => `${v}${v && !v.endsWith(" ") ? " " : ""}${tag} `);
      return;
    }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    const next = `${text.slice(0, start)}${tag} ${text.slice(end)}`;
    setText(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + tag.length + 1;
      el.setSelectionRange(pos, pos);
    });
  };

  const generate = async () => {
    if (text.trim().length < 2 || !voiceId) return;
    setGenerating(true);
    try {
      const { data } = await apiClient.post("/speech/generate", {
        text: text.trim(),
        voice_id: voiceId,
        model,
      });
      onGenerated?.(data);
      toast.success(t("voiceDone"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Voice generation failed");
      if (e.response?.status === 502) window.dispatchEvent(new CustomEvent("aurafx:engine-check"));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="voice-generator">
      <Textarea
        data-testid="voice-text-input"
        ref={textRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={800}
        placeholder={t("voicePlaceholder")}
        className="min-h-[90px] bg-[color:var(--surface-2)] border-white/10 text-white placeholder:text-white/30 resize-none"
      />

      {/* Model switcher */}
      <div className="mt-5">
        <label className="text-xs font-mono-num uppercase tracking-widest text-white/50 block mb-2">{t("voiceModel")}</label>
        <div className="grid grid-cols-2 gap-2" data-testid="voice-model-switcher">
          {[
            { id: "v3", label: t("voiceModelV3"), hint: t("voiceModelV3Hint"), Icon: Sparkle },
            { id: "v2", label: t("voiceModelV2"), hint: t("voiceModelV2Hint"), Icon: ShieldCheck },
          ].map(({ id, label, hint, Icon }) => (
            <button
              key={id}
              type="button"
              data-testid={`voice-model-${id}`}
              onClick={() => setModel(id)}
              className={`text-left rounded-xl border px-3.5 py-2.5 transition-colors ${
                model === id
                  ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 shadow-[0_0_14px_-4px_rgba(229,9,20,0.7)]"
                  : "border-white/10 bg-[color:var(--surface-2)] hover:border-white/25"
              }`}
            >
              <span className="flex items-center gap-1.5 text-sm font-medium text-white">
                <Icon size={15} weight="fill" className={model === id ? "text-[color:var(--accent)]" : "text-white/50"} />
                {label}
              </span>
              <span className="block text-xs text-white/40 mt-0.5 leading-snug">{hint}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Emotion tags (v3 only) */}
      <div className={`mt-5 transition-opacity ${model === "v3" ? "opacity-100" : "opacity-40"}`}>
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-xs font-mono-num uppercase tracking-widest text-white/50">{t("voiceTags")}</label>
          <span className="text-[11px] text-white/35">{t("voiceTagsHint")}</span>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="voice-emotion-tags">
          {EMOTION_TAGS.map(({ tag, key }) => (
            <button
              key={key}
              type="button"
              data-testid={`emotion-tag-${key}`}
              disabled={model !== "v3"}
              onClick={() => insertTag(tag)}
              className="px-3 py-1.5 rounded-full text-xs font-mono-num border border-white/10 bg-[color:var(--surface-2)] text-white/75 hover:border-[color:var(--accent)] hover:text-white transition-colors disabled:cursor-not-allowed"
            >
              {t(key)}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 max-w-xs">
        <label className="text-xs font-mono-num uppercase tracking-widest text-white/50 block mb-2">{t("voiceSelect")}</label>
        <Select value={voiceId} onValueChange={setVoiceId}>
          <SelectTrigger data-testid="voice-select" className="bg-[color:var(--surface-2)] border-white/10 text-white">
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent className="bg-[color:var(--surface-2)] border-white/10 text-white">
            {voices.map((v) => (
              <SelectItem key={v.voice_id} value={v.voice_id} className="focus:bg-white/10">
                {v.name} — {v.desc}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        data-testid="generate-voice-btn"
        onClick={generate}
        disabled={generating || text.trim().length < 2 || !voiceId || credits < 1}
        className="btn-primary w-full mt-6 py-3.5 rounded-xl font-medium inline-flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {generating ? (
          <><ArrowsClockwise size={18} className="animate-spin" /> {t("generating")}</>
        ) : (
          <><Microphone size={18} weight="fill" /> {t("generateVoice")} · 1 {t("creditsShort")}</>
        )}
      </button>
    </div>
  );
}
